<template>
  <div class="cleanup-component">

    <cleanup-dialog-box
      v-if="isCleanupDialogBoxVisible"
      :is-cleanup-all="isCleanupAll"
      :cleanup-box="cleanupBox"
      @handleClose="onClose"
      @goToBackups="onGoToBackups"
      @submitCleanup="onSubmitCleanup"
    />

    <cleanup-confirm-dialog-box
      v-if="isCleanupConfirmDialogBoxVisible"
      @handleClose="onClose"
      @submitCleanupConfirm="onSubmitCleanupConfirm"
    />

    <cleanup-progress-dialog-box
      v-if="isCleanupProgressDialogBoxVisible"
      @handleClose="onClose"
    />

    <el-dialog
      v-if="isCleanupSuccessDialogBoxVisible"
      :title="$t('wrh-dashboard.cleanup-notification-dialog-box.title')"
      @close="onClose"
      :show-close="false"
      visible>
      <div class="mx-24 my-24 el-text el-text--size-xs">
        <div style="display: inline-flex;">
          <div>
            <el-icon
              class="mr-16"
              color="fixed-success"
              name="i-check-circle--16"
            />
          </div>
          <div>
            {{ $t('wrh-dashboard.cleanup-notification-dialog-box.message.success') }}
          </div>
        </div>
      </div>
      <template #footer>
        <el-button
          @click="onClose"
          native-type="button">
          {{ $t('wrh-dashboard.form.button.done') }}
        </el-button>
      </template>
    </el-dialog>

    <el-dialog
      v-if="isCleanupErrorDialogBoxVisible || isBoxLockedErrorDialogBoxVisible"
      :title="$t('wrh-dashboard.cleanup-notification-dialog-box.title')"
      @close="onClose"
      :show-close="false"
      visible>
      <div class="mx-24 my-24 el-text el-text--size-xs">
        <div style="margin-top: 13px; margin-bottom: -4px;">
          <span>
            <el-icon
              class="mr-8"
              color="fixed-danger"
              name="i-times-circle--16"
            />
            {{
              isCleanupErrorDialogBoxVisible ?
                $t('wrh-dashboard.cleanup-notification-dialog-box.message.fail') :
                $t('wrh-dashboard.cleanup-notification-dialog-box.message.box-locked')
            }}
          </span>
        </div>
      </div>
      <template #footer>
        <el-button
          @click="onClose"
          native-type="button"
          type="secondary">
          {{ $t('wrh-dashboard.form.button.close') }}
        </el-button>
      </template>
    </el-dialog>

    <el-dialog
      v-if="isCleanupAlreadyRunningDialogBoxVisible"
      :title="$t('wrh-dashboard.cleanup-notification-dialog-box.title')"
      @close="onClose"
      :show-close="true"
      visible>
      <div class="mx-24 my-24 el-text el-text--size-xs">
        <div style="margin-top: 13px; margin-bottom: -4px;">
          <span>
            {{ $t('wrh-dashboard.cleanup-notification-dialog-box.message.wait') }}
          </span>
        </div>
      </div>
      <template #footer>
        <el-button
          @click="onClose"
          native-type="button"
          type="secondary">
          {{ $t('wrh-dashboard.form.button.close') }}
        </el-button>
      </template>
    </el-dialog>

  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';

import {
  CLEANUP_ARCHIVE,
  FETCH_CLEANUP_STAGE
} from '../store/action-types';

import {
  SUCCESS_STATUS,
  ERROR_STATUS, LOCKED_STATUS
} from '../store/action-status';

import {
  RUNNING_CLEANUP_STAGE,
  ACCESSIBLE_CLEANUP_STAGE
} from '../store/cleanup-stage';

import {
  CLEANUP_HIDE_ALL,
  CLEANUP_OPTIONS,
  CLEANUP_CONFIRM,
  CLEANUP_PROGRESS,
  CLEANUP_SUCCESS,
  CLEANUP_ERROR,
  BACKUP_LOCKED,
  CLEANUP_ALREADY_RUNNING
} from '../store/active-cleanup-dialog';

import CleanupDialogBox from './CleanupDialogBox';
import CleanupConfirmDialogBox from './CleanupConfirmDialogBox';
import CleanupProgressDialogBox from './CleanupProgressDialogBox';

const { mapGetters, mapActions } = createNamespacedHelpers('wrh-dashboard');

export default {
  name: 'CleanupComponent',
  components: {
    CleanupDialogBox,
    CleanupConfirmDialogBox,
    CleanupProgressDialogBox
  },
  props: {
    isCleanupAll: {
      type: Boolean,
      default: true,
      required: false
    },
    cleanupBox: {
      type: Object,
      default: () => ({}),
      required: false
    }
  },
  data() {
    return {
      activeCleanupDialog: CLEANUP_HIDE_ALL,
      cleanupOptions: {}
    };
  },
  computed: {
    ...mapGetters([
      'getCleanupStatus',
      'getCleanupStage',
      'getSpaceFreeAfterCleanup'
    ]),
    isCleanupDialogBoxVisible() {
      return this.activeCleanupDialog === CLEANUP_OPTIONS;
    },
    isCleanupConfirmDialogBoxVisible() {
      return this.activeCleanupDialog === CLEANUP_CONFIRM;
    },
    isCleanupProgressDialogBoxVisible() {
      return this.activeCleanupDialog === CLEANUP_PROGRESS;
    },
    isCleanupSuccessDialogBoxVisible() {
      return this.activeCleanupDialog === CLEANUP_SUCCESS;
    },
    isCleanupErrorDialogBoxVisible() {
      return this.activeCleanupDialog === CLEANUP_ERROR;
    },
    isBoxLockedErrorDialogBoxVisible() {
      return this.activeCleanupDialog === BACKUP_LOCKED;
    },
    isCleanupAlreadyRunningDialogBoxVisible() {
      return this.activeCleanupDialog === CLEANUP_ALREADY_RUNNING;
    },
    spaceFreeText() {
      return this.$options.filters.size(this.getSpaceFreeAfterCleanup);
    }
  },
  watch: {
    getCleanupStage(stage) {
      if (stage === ACCESSIBLE_CLEANUP_STAGE) {
        this.showCleanupDialog(CLEANUP_OPTIONS);
      } else if (stage === RUNNING_CLEANUP_STAGE) {
        this.showCleanupDialog(CLEANUP_ALREADY_RUNNING);
      }
    },
    getCleanupStatus(status) {
      if (status === SUCCESS_STATUS) {
        this.showCleanupDialog(CLEANUP_SUCCESS);
      } else if (status === ERROR_STATUS) {
        this.showCleanupDialog(CLEANUP_ERROR);
      } else if (status === LOCKED_STATUS) {
        this.showCleanupDialog(BACKUP_LOCKED);
      }
    }
  },
  methods: {
    ...mapActions({
      cleanupArchive: CLEANUP_ARCHIVE,
      fetchCleanupStage: FETCH_CLEANUP_STAGE
    }),
    showCleanupDialog(type) {
      this.activeCleanupDialog = type;
    },
    onSubmitCleanup(cleanupOptions) {
      this.cleanupOptions = cleanupOptions;
      this.showCleanupDialog(CLEANUP_CONFIRM);
    },
    onSubmitCleanupConfirm() {
      this.showCleanupDialog(CLEANUP_PROGRESS);
      this.cleanupArchive(this.cleanupOptions);
    },
    onGoToBackups() {
      this.$emit('goToMenuItem', { id: 'BACKUPS' });
      this.onClose();
    },
    onClose() {
      this.showCleanupDialog(CLEANUP_HIDE_ALL);
    }
  }
};
</script>

<style lang="scss" scoped>

</style>
